import * as moment from 'moment';
import type * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import 'moment/locale/de';
import { cn } from '@utils/helpers';
import { ChevronDownIcon } from 'lucide-react';
import { ArchiveListMonthGroup } from '../archiveListMonthGroup';
import type { ArchiveListYearGroupPropsType } from './types';

const ArchiveListYearGroup: React.FC<ArchiveListYearGroupPropsType> = ({ documents, yearLabel, year, isVisible: isVisibleProp, isFirst, language }) => {
  const [isVisible, setIsVisible] = useState(isVisibleProp || isFirst);

  useEffect(() => {
    if (isVisibleProp || isFirst) {
      setIsVisible(true);
    }
  }, [isVisibleProp, isFirst]);

  const groupDocumentsByMonth = useCallback(
    (docs: Array<any>): { [key: number]: any[] } =>
      docs.reduce<any>((prev, fileLink) => {
        const dateFormats = ['DD.MM.YYYY', 'MM/DD/YYYY'];
        const month = Number.parseInt(moment(fileLink.date, dateFormats).locale(language).format('M'), 10);

        if (prev[month] !== undefined) {
          prev[month].push(fileLink);
        } else {
          prev[month] = [fileLink];
        }
        return prev;
      }, {}),
    [language],
  );

  const toggleVisibility = useCallback((e: any): void => {
    setIsVisible((prev) => !prev);
  }, []);

  const groupedDocuments = useMemo(() => groupDocumentsByMonth(documents), [documents, groupDocumentsByMonth]);

  const sortedYearGroup = useMemo(() => Object.keys(groupedDocuments).sort((a, b) => (Number.parseInt(a, 10) < Number.parseInt(b, 10) ? 1 : -1)), [groupedDocuments]);

  return (
    <div>
      <h4
        onClick={toggleVisibility}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleVisibility(e);
          }
        }}
        className="cursor-pointer border-b border-[#ececec] pb-5 text-xl text-gray-500 flex items-center gap-6"
      >
        <span>{`${yearLabel} ${year}`}</span>
        <ChevronDownIcon className={cn('size-4 text-gray-500', isVisible && 'rotate-180')} />
      </h4>
      <div className="pt-5">
        <div className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out ${isVisible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className="overflow-hidden">
            {sortedYearGroup.map((month, index) => (
              <ArchiveListMonthGroup key={month} documents={groupedDocuments[month]} month={moment(month, 'M').locale(language).format('MMMM')} isVisible={isVisibleProp} isFirst={isFirst && index === 0} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export { ArchiveListYearGroup };
